/* Per-name bottle colors with one shared size. No names or selections are
 * persisted or sent anywhere; row IDs own their color for this page session.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.BloomProductVariants = factory();
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  function create(catalog, initialVariant) {
    if (!Array.isArray(catalog) || !catalog.length) throw new TypeError('Provide the product variant catalog.');
    const variants = new Map(), combinations = new Map(), knownColors = new Set();
    for (const source of catalog) {
      if (!source || typeof source.id !== 'string' || !source.id ||
          typeof source.size !== 'string' || !source.size ||
          typeof source.color !== 'string' || !source.color ||
          !Number.isSafeInteger(source.price) || source.price < 0 || typeof source.available !== 'boolean') {
        throw new TypeError('Each variant needs an exact ID, size, color, integer price and availability.');
      }
      if (!combinations.has(source.size)) combinations.set(source.size, new Map());
      const colors = combinations.get(source.size);
      if (variants.has(source.id) || colors.has(source.color)) throw new RangeError('Variant IDs and size/color pairs must be unique.');
      const variant = Object.freeze({ ...source });
      variants.set(variant.id, variant); colors.set(variant.color, variant); knownColors.add(variant.color);
    }
    const initialId = typeof initialVariant === 'string' ? initialVariant : initialVariant?.id;
    const initial = variants.get(initialId);
    if (!initial) throw new RangeError('The initial variant must belong to this catalog.');

    let sharedSize = initial.size, liveIds = [];
    const colorsByRow = new Map();
    function sync(rows, activeId) {
      if (!Array.isArray(rows)) throw new TypeError('Provide the current name rows.');
      const ids = rows.map(row => row?.id);
      if (ids.some(id => typeof id !== 'string' || !id) || new Set(ids).size !== ids.length) {
        throw new RangeError('Every name row needs a unique stable ID.');
      }
      // Choose the donor before adding rows. A new active row inherits from an
      // existing row, and future focus/rename/reorder changes cannot recolor it.
      const donorId = ids.includes(activeId) && colorsByRow.has(activeId)
        ? activeId : ids.find(id => colorsByRow.has(id));
      const inheritedColor = donorId === undefined ? initial.color : colorsByRow.get(donorId);
      for (const id of ids) if (!colorsByRow.has(id)) colorsByRow.set(id, inheritedColor);
      // Removed entries stay in colorsByRow so Undo restores their own color.
      liveIds = ids;
    }
    function setColor(rowId, color) {
      if (!colorsByRow.has(rowId)) throw new RangeError('Sync the name row before selecting its color.');
      if (!knownColors.has(color)) throw new RangeError('Choose an exact color from the catalog.');
      colorsByRow.set(rowId, color);
    }
    function setSize(size) {
      if (!combinations.has(size)) throw new RangeError('Choose an exact size from the catalog.');
      sharedSize = size;
    }
    function colorFor(rowId) { return colorsByRow.get(rowId) ?? null; }
    function variantFor(rowId) {
      const color = colorFor(rowId);
      return color === null ? null : combinations.get(sharedSize).get(color) ?? null;
    }
    function totals() {
      let total = 0;
      const missingRowIds = [], unavailableRowIds = [];
      for (const rowId of liveIds) {
        const variant = variantFor(rowId);
        if (!variant) { missingRowIds.push(rowId); continue; }
        total += variant.price;
        if (!Number.isSafeInteger(total)) throw new RangeError('The combined price exceeds the supported integer range.');
        if (!variant.available) unavailableRowIds.push(rowId);
      }
      const complete = missingRowIds.length === 0;
      return { count: liveIds.length, total: complete ? total : null, complete,
        available: liveIds.length > 0 && complete && unavailableRowIds.length === 0,
        missingRowIds, unavailableRowIds };
    }
    return Object.freeze({ sync, setColor, setSize, variantFor, colorFor, totals,
      get size() { return sharedSize; } });
  }
  return Object.freeze({ create });
});
